import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, Calendar } from 'lucide-react';
import { generateOffersSchema, generateFallMaintenanceEventSchema, generateBreadcrumbSchema } from '../utils/schemaMarkup';
import { useSchemaMarkup } from '../utils/useSchemaMarkup';
import { getActiveSpecials, getAdSlides } from '../data/specials';
import { SpecialCard } from '../components/specials/SpecialCard';
import { FinancingBanner } from '../components/FinancingBanner';

const SpecialsPage = () => {
  const specials = getActiveSpecials();
  const offersSchema = generateOffersSchema(specials);
  const eventSchema = generateFallMaintenanceEventSchema();
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://comfortmasterheating.com' },
    { name: 'Specials', url: 'https://comfortmasterheating.com/specials' }
  ]);

  useSchemaMarkup([offersSchema, eventSchema, breadcrumbSchema], 'specials');

  return (
    <main>
      <Helmet>
        <title>HVAC Specials & Promotions | ComfortMaster Heating & Air | Lexington KY</title>
        <meta name="description" content="Save on HVAC services with our current specials. $99 tune-ups, 10% off for new customers, veterans, and seniors. Limited time offers in Central Kentucky." />
        <link rel="canonical" href="https://comfortmasterheating.com/specials" />
      </Helmet>

      <section className="bg-slate-900 text-white py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Limited Time Offers</span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl mt-3 sm:mt-4 mb-4 sm:mb-6" data-testid="specials-title">Specials & Promotions</h1>
            <p className="text-base sm:text-xl text-stone-300 mb-6 sm:mb-8">
              Take advantage of our current specials and exclusive seasonal offers. Save on tune-ups, repairs, and installations. Tap any offer to see details or book online.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="specials-estimate-btn">
                <Calendar size={20} /> Schedule Service
              </Link>
              <a href="tel:8593576803" className="btn-secondary inline-flex items-center justify-center gap-2 bg-transparent border-white/30 text-white hover:bg-white hover:text-slate-900" data-testid="specials-call-btn">
                <Phone size={20} /> (859) 357-6803
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="pt-10 pb-2 sm:py-20 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {specials.length === 0 && (
            <p className="text-center text-slate-600 py-12" data-testid="specials-empty">New offers are coming soon. Call (859) 357-6803 to ask about current savings.</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-testid="specials-grid">
            {getAdSlides().map(({ special, image, v }, i) => <SpecialCard key={`${special.id}-${v}`} special={special} image={image} variant={v} index={i} />)}
          </div>

          <div className="mt-10 sm:mt-16 bg-white p-6 sm:p-8 rounded-2xl shadow-sm text-center">
            <h2 className="font-heading font-bold text-2xl text-slate-900 mb-4">Ready to Save?</h2>
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
              Contact us today to take advantage of these special offers. Our team is ready to help you with all your HVAC needs.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="specials-cta-btn">
              Get Your Free Estimate
            </Link>
          </div>
        </div>
      </section>

      <FinancingBanner variant="specials" />
    </main>
  );
};

export default SpecialsPage;
